import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import TopBar from "@/components/TopBar";
import StatusBadge from "@/components/StatusBadge";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

const FALLBACK_IMG =
  "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=900";

export default function SpecialGifts() {
  const { code } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/products?special=true`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5">
        <button
          onClick={() => navigate(`/event/${code}`)}
          data-testid="special-back"
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("product.backToEvent")}
        </button>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#EFE3F5] text-[#6B2C8F] flex items-center justify-center">
            <Star className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] leading-tight">
              {t("special.title")}
            </h1>
            <p className="mt-1 text-[#5C4664]">{t("special.sub")}</p>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="py-16 text-center text-[#5C4664]">{t("common.loading")}</div>
          ) : products.length === 0 ? (
            <div data-testid="special-empty" className="py-16 text-center text-[#5C4664]">
              {t("special.noProducts")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p) => (
                <Link
                  key={p.id}
                  to={`/event/${code}/shop/product/${p.id}`}
                  data-testid={`special-card-${p.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-[#F2E2EC] hover:border-[#6B2C8F]/50 hover:shadow-lg transition-all"
                >
                  <div className="aspect-video w-full bg-[#EFE3F5] overflow-hidden relative">
                    <img
                      src={p.image_url || FALLBACK_IMG}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <StatusBadge status="ouvert" />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-[#6B2C8F]">
                      <Star className="w-3 h-3" /> Premium
                    </div>
                    <h3 className="mt-1 font-display text-sm sm:text-base font-black text-[#2A0E3C] leading-snug">
                      {p.name}
                    </h3>
                    {p.description && (
                      <p className="mt-2 text-xs text-[#5C4664] leading-relaxed line-clamp-3">
                        {p.description}
                      </p>
                    )}
                    <div className="mt-4 flex items-baseline justify-between">
                      <div>
                        <span className="font-display text-lg sm:text-xl font-black text-[#6B2C8F]">
                          ${(p.final_price ?? p.price).toFixed(0)}
                        </span>
                        <div className="text-[9px] text-[#5C4664]">{t("common2.allInclusive")}</div>
                      </div>
                      <span className="text-[10px] text-[#D81B8C] font-semibold">
                        {t("shop.orderNow")} →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
